import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { exercises } from '../../api/client'
import { useAuth } from '../../hooks/useAuth'
import PageHeader from '../../components/PageHeader'
import {
  PageLayout, Card, Badge, Tag, Modal, Button, IconButton,
  TextInput, TextArea, Select, SkeletonCard, EmptyState, ConfirmModal
} from '../../components/ui'
import { muscleGroups } from '../../utils/constants'

const DIFFICULTY_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
]

function getDifficultyVariant(difficulty) {
  switch (difficulty) {
    case 'beginner': return 'success'
    case 'intermediate': return 'warning'
    case 'advanced': return 'error'
    default: return 'default'
  }
}

function ExerciseCard({ exercise, onClick }) {
  return (
    <Card hover onClick={onClick}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-medium text-indices-bright">{exercise.name}</h3>
          <p className="text-sm text-indices-medium mt-1">{exercise.muscleGroup}</p>
        </div>
        <Badge variant={getDifficultyVariant(exercise.difficulty)}>
          {exercise.difficulty}
        </Badge>
      </div>
      {exercise.description && (
        <p className="text-sm text-indices-medium mt-2 line-clamp-2">{exercise.description}</p>
      )}
      {exercise.equipment && (
        <p className="text-xs text-indices-muted mt-2">Equipment: {exercise.equipment}</p>
      )}
    </Card>
  )
}

function ExerciseModal({ exercise, isOpen, onClose, onSave, onDelete, isAdmin }) {
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    muscleGroup: '',
    difficulty: 'intermediate',
    equipment: '',
    instructions: '',
  })

  const handleStartEdit = () => {
    setFormData({
      name: exercise.name || '',
      description: exercise.description || '',
      muscleGroup: exercise.muscleGroup || '',
      difficulty: exercise.difficulty || 'intermediate',
      equipment: exercise.equipment || '',
      instructions: exercise.instructions || '',
    })
    setIsEditing(true)
  }

  const handleSave = () => {
    onSave({
      id: exercise.id,
      name: formData.name,
      description: formData.description,
      muscleGroup: formData.muscleGroup,
      difficulty: formData.difficulty,
      equipmentNeeded: formData.equipment,
      instructions: formData.instructions,
    })
    setIsEditing(false)
  }

  const handleClose = () => {
    setIsEditing(false)
    onClose()
  }

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const formatMuscleGroup = (group) => group.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  const muscleGroupOptions = muscleGroups.map(group => ({
    value: formatMuscleGroup(group),
    label: formatMuscleGroup(group),
  }))

  if (!exercise) return null

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={isEditing ? 'Edit Exercise' : exercise.name}
        size="lg"
      >
        {isEditing ? (
          <div className="space-y-4">
            <TextInput
              label="Name"
              value={formData.name}
              onChange={handleChange('name')}
              required
            />

            <TextArea
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              rows={2}
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Muscle Group"
                value={formData.muscleGroup}
                onChange={handleChange('muscleGroup')}
                options={muscleGroupOptions}
              />

              <Select
                label="Difficulty"
                value={formData.difficulty}
                onChange={handleChange('difficulty')}
                options={DIFFICULTY_LEVELS}
              />
            </div>

            <TextInput
              label="Equipment"
              value={formData.equipment}
              onChange={handleChange('equipment')}
              placeholder="e.g., Barbell, Dumbbells, None"
            />

            <TextArea
              label="Instructions"
              value={formData.instructions}
              onChange={handleChange('instructions')}
              rows={4}
              placeholder="Step-by-step instructions..."
            />

            <div className="flex gap-3 pt-2">
              <Button variant="secondary" fullWidth onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button fullWidth onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant={getDifficultyVariant(exercise.difficulty)}>
                {exercise.difficulty}
              </Badge>
              <Badge variant="default">
                {exercise.muscleGroup}
              </Badge>
            </div>

            {exercise.description && (
              <div>
                <h3 className="text-sm font-medium text-indices-bright mb-1">Description</h3>
                <p className="text-sm text-indices-medium">{exercise.description}</p>
              </div>
            )}

            {exercise.equipment && (
              <div>
                <h3 className="text-sm font-medium text-indices-bright mb-1">Equipment</h3>
                <p className="text-sm text-indices-medium">{exercise.equipment}</p>
              </div>
            )}

            {exercise.instructions && (
              <div>
                <h3 className="text-sm font-medium text-indices-bright mb-1">Instructions</h3>
                <p className="text-sm text-indices-medium whitespace-pre-line">{exercise.instructions}</p>
              </div>
            )}

            {isAdmin && (
              <div className="flex gap-3 pt-4 border-t border-dial-border">
                <Button fullWidth onClick={handleStartEdit}>
                  Edit
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false)
          onDelete(exercise.id)
        }}
        title="Delete Exercise"
        message="Are you sure you want to delete this exercise?"
        confirmText="Delete"
        variant="danger"
      />
    </>
  )
}

function NewExerciseModal({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    muscleGroup: 'Chest',
    difficulty: 'intermediate',
    equipment: '',
    instructions: '',
  })

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      name: formData.name,
      description: formData.description,
      muscleGroup: formData.muscleGroup,
      difficulty: formData.difficulty,
      equipmentNeeded: formData.equipment,
      instructions: formData.instructions,
    })
    // Reset form
    setFormData({
      name: '',
      description: '',
      muscleGroup: 'Chest',
      difficulty: 'intermediate',
      equipment: '',
      instructions: '',
    })
  }

  const formatMuscleGroup = (group) => group.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  const muscleGroupOptions = muscleGroups.map(group => ({
    value: formatMuscleGroup(group),
    label: formatMuscleGroup(group),
  }))

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Exercise" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <TextInput
          label="Name"
          value={formData.name}
          onChange={handleChange('name')}
          required
        />

        <TextArea
          label="Description"
          value={formData.description}
          onChange={handleChange('description')}
          rows={2}
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Muscle Group"
            value={formData.muscleGroup}
            onChange={handleChange('muscleGroup')}
            options={muscleGroupOptions}
          />

          <Select
            label="Difficulty"
            value={formData.difficulty}
            onChange={handleChange('difficulty')}
            options={DIFFICULTY_LEVELS}
          />
        </div>

        <TextInput
          label="Equipment"
          value={formData.equipment}
          onChange={handleChange('equipment')}
          placeholder="e.g., Barbell, Dumbbells, None"
        />

        <TextArea
          label="Instructions"
          value={formData.instructions}
          onChange={handleChange('instructions')}
          rows={4}
          placeholder="Step-by-step instructions..."
        />

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="secondary" fullWidth onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" fullWidth>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function ExerciseListPage() {
  const [filter, setFilter] = useState('All')
  const [selectedExercise, setSelectedExercise] = useState(null)
  const [showNewModal, setShowNewModal] = useState(false)
  const { trainer } = useAuth()
  const queryClient = useQueryClient()

  const isAdmin = trainer?.role === 'admin'

  const { data: exerciseList, isLoading } = useQuery({
    queryKey: ['exercises'],
    queryFn: exercises.list,
  })

  const createMutation = useMutation({
    mutationFn: exercises.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] })
      setShowNewModal(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) => exercises.update(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] })
      setSelectedExercise(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => exercises.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] })
      setSelectedExercise(null)
    },
  })

  const filteredExercises = exerciseList?.filter(ex =>
    filter === 'All' || ex.muscleGroup === filter
  ) || []

  // Build filter options - capitalize muscle group names
  const formatMuscleGroup = (group) => group.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  const filterOptions = ['All', ...muscleGroups.map(formatMuscleGroup)]

  return (
    <PageLayout>
      <PageHeader
        title="Exercises"
        showBack
        backTo="/"
        actions={
          isAdmin && (
            <IconButton
              onClick={() => setShowNewModal(true)}
              label="New exercise"
            >
              <svg className="w-6 h-6 text-lume-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </IconButton>
          )
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
          {filterOptions.map(group => (
            <Tag
              key={group}
              color="primary"
              selected={filter === group}
              onClick={() => setFilter(group)}
            >
              {group}
            </Tag>
          ))}
        </div>

        {/* Exercise List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : filteredExercises.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h2m12 0h2M6 8v8m12-8v8M8 8h8m-8 8h8M6 12h2m8 0h2" />
              </svg>
            }
            title="No exercises found"
            message={filter !== 'All' ? `No exercises in ${filter} category` : 'No exercises available'}
          />
        ) : (
          <div className="space-y-3">
            {filteredExercises.map(exercise => (
              <ExerciseCard
                key={exercise.id}
                exercise={exercise}
                onClick={() => setSelectedExercise(exercise)}
              />
            ))}
          </div>
        )}
      </div>

      <ExerciseModal
        exercise={selectedExercise}
        isOpen={!!selectedExercise}
        onClose={() => setSelectedExercise(null)}
        onSave={updateMutation.mutate}
        onDelete={deleteMutation.mutate}
        isAdmin={isAdmin}
      />

      {isAdmin && (
        <NewExerciseModal
          isOpen={showNewModal}
          onClose={() => setShowNewModal(false)}
          onSave={createMutation.mutate}
        />
      )}
    </PageLayout>
  )
}
